<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  images: {
    type: Array,
    default: () => [],
  },
  initialIndex: {
    type: Number,
    default: 0,
  },
  review: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close', 'change-index']);

const currentIndex = ref(props.initialIndex);
const isCaptionExpanded = ref(false);

// Watch for changes in initialIndex or isOpen
watch(
  () => props.initialIndex,
  (val) => {
    currentIndex.value = Math.max(0, Math.min(val, (props.images?.length || 1) - 1));
  }
);

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      currentIndex.value = Math.max(0, Math.min(props.initialIndex, (props.images?.length || 1) - 1));
      isCaptionExpanded.value = false;
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }
);

const currentImage = computed(() => {
  if (!props.images || !props.images.length) return null;
  const item = props.images[currentIndex.value];
  return typeof item === 'string' ? item : item?.url || null;
});

const totalImages = computed(() => props.images?.length || 0);

const hasPrev = computed(() => currentIndex.value > 0);
const hasNext = computed(() => currentIndex.value < totalImages.value - 1);

const goToPrev = () => {
  if (hasPrev.value) {
    currentIndex.value--;
    emit('change-index', currentIndex.value);
  }
};

const goToNext = () => {
  if (hasNext.value) {
    currentIndex.value++;
    emit('change-index', currentIndex.value);
  }
};

const selectIndex = (index) => {
  if (index >= 0 && index < totalImages.value) {
    currentIndex.value = index;
    emit('change-index', currentIndex.value);
  }
};

const closeModal = () => {
  emit('close');
};

// Touch Gestures for Mobile Swipe
let touchStartX = 0;
let touchStartY = 0;
let touchEndX = 0;
let touchEndY = 0;

const handleTouchStart = (e) => {
  if (!e.touches || e.touches.length === 0) return;
  touchStartX = e.touches[0].clientX;
  touchStartY = e.touches[0].clientY;
  touchEndX = touchStartX;
  touchEndY = touchStartY;
};

const handleTouchMove = (e) => {
  if (!e.touches || e.touches.length === 0) return;
  touchEndX = e.touches[0].clientX;
  touchEndY = e.touches[0].clientY;
};

const handleTouchEnd = () => {
  const deltaX = touchEndX - touchStartX;
  const deltaY = touchEndY - touchStartY;
  // If horizontal swipe is dominant and above threshold (40px)
  if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 40) {
    if (deltaX > 0) {
      goToPrev();
    } else {
      goToNext();
    }
  }
};

// Keyboard listener
const handleKeydown = (e) => {
  if (!props.isOpen) return;
  if (e.key === 'Escape') {
    closeModal();
  } else if (e.key === 'ArrowLeft') {
    goToPrev();
  } else if (e.key === 'ArrowRight') {
    goToNext();
  }
};

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
  document.body.style.overflow = '';
});
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[999] bg-black/95 backdrop-blur-xl flex flex-col justify-between select-none overflow-hidden"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <!-- Top Navigation & Info Header -->
        <header class="flex items-center justify-between p-3 sm:p-5 bg-gradient-to-b from-black/80 via-black/40 to-transparent z-20 shrink-0 gap-3">
          <!-- Author & Restaurant Badge -->
          <div class="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
            <div class="avatar placeholder shrink-0">
              <div class="bg-primary/30 text-primary-content rounded-full w-8 h-8 sm:w-10 sm:h-10 ring-1 ring-white/20 flex items-center justify-center text-center">
                <span class="text-sm sm:text-base md:text-lg font-extrabold uppercase select-none leading-none text-center">{{ review.user?.name ? review.user.name.charAt(0) : 'U' }}</span>
              </div>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <h4 class="font-bold text-xs sm:text-sm text-white truncate">{{ review.user?.name || 'Foodie' }}</h4>
                <span v-if="review.rating" class="inline-flex items-center gap-0.5 text-amber-400 text-xs font-semibold shrink-0">
                  <span>★</span>
                  <span>{{ review.rating }}/5</span>
                </span>
              </div>
              <p class="text-[11px] sm:text-xs text-white/70 truncate flex items-center gap-1">
                <span>📍 {{ review.restaurant_name || 'Restaurant' }}</span>
                <span v-if="review.address" class="hidden sm:inline opacity-60">• {{ review.address }}</span>
              </p>
            </div>
          </div>

          <!-- Counter Pill (Instagram Style: 1 / 5) -->
          <div class="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-white/90 text-xs sm:text-sm font-semibold tracking-wider shrink-0 border border-white/10">
            {{ currentIndex + 1 }} / {{ totalImages }}
          </div>

          <!-- Close Action -->
          <div class="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              class="btn btn-circle btn-sm sm:btn-md btn-ghost text-white bg-white/10 hover:bg-white/20 active:scale-95 border-none"
              title="Close Gallery (Esc)"
              @click="closeModal"
            >
              ✕
            </button>
          </div>
        </header>

        <!-- Center Main Image Stage -->
        <main class="relative flex-1 flex items-center justify-center p-2 sm:p-4 overflow-hidden">
          <!-- Previous Button (Desktop / Tablet) -->
          <button
            type="button"
            class="absolute left-2 sm:left-6 z-20 btn btn-circle btn-md sm:btn-lg bg-black/50 hover:bg-black/80 text-white border border-white/20 shadow-2xl transition-all duration-200 active:scale-90"
            :class="{ 'opacity-30 pointer-events-none': !hasPrev }"
            title="Previous Image"
            @click.stop="goToPrev"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 sm:h-7 sm:w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <!-- Active Photo with Transition -->
          <div class="relative max-h-[70vh] sm:max-h-[76vh] max-w-[95vw] sm:max-w-[85vw] flex items-center justify-center group">
            <Transition name="scale-fade" mode="out-in">
              <img
                :key="currentImage"
                :src="currentImage"
                alt="Food Review Fullscreen Photo"
                class="max-h-[68vh] sm:max-h-[75vh] max-w-full rounded-2xl object-contain shadow-2xl transition-transform duration-300"
              >
            </Transition>
          </div>

          <!-- Next Button (Desktop / Tablet) -->
          <button
            type="button"
            class="absolute right-2 sm:right-6 z-20 btn btn-circle btn-md sm:btn-lg bg-black/50 hover:bg-black/80 text-white border border-white/20 shadow-2xl transition-all duration-200 active:scale-90"
            :class="{ 'opacity-30 pointer-events-none': !hasNext }"
            title="Next Image"
            @click.stop="goToNext"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 sm:h-7 sm:w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </main>

        <!-- Bottom Controls, Instagram Dots, Thumbnails & Caption Drawer -->
        <footer class="p-3 sm:p-5 bg-gradient-to-t from-black/95 via-black/70 to-transparent z-20 shrink-0 space-y-3">
          
          <!-- Instagram Style Indicator Dots (Mobile) -->
          <div v-if="totalImages > 1" class="flex items-center justify-center gap-1.5 sm:hidden">
            <button
              v-for="(_, idx) in images"
              :key="idx"
              type="button"
              class="h-1.5 rounded-full transition-all duration-200"
              :class="idx === currentIndex ? 'w-5 bg-primary' : 'w-1.5 bg-white/40'"
              @click="selectIndex(idx)"
            />
          </div>

          <!-- Interactive Thumbnail Strip (Desktop / Tablet) -->
          <div v-if="totalImages > 1" class="hidden sm:flex items-center justify-center gap-2 overflow-x-auto py-1 no-scrollbar max-w-2xl mx-auto">
            <button
              v-for="(img, idx) in images"
              :key="idx"
              type="button"
              class="relative w-14 h-14 rounded-xl overflow-hidden border-2 transition-all duration-200 shrink-0 hover:opacity-100"
              :class="idx === currentIndex 
                ? 'border-primary scale-105 shadow-lg shadow-primary/30 opacity-100 ring-2 ring-primary/40' 
                : 'border-white/20 opacity-50 hover:border-white/50'"
              @click="selectIndex(idx)"
            >
              <img :src="typeof img === 'string' ? img : img.url" alt="Thumb" class="w-full h-full object-cover">
            </button>
          </div>

          <!-- Expandable Review Caption Box -->
          <div v-if="review.details" class="max-w-2xl mx-auto bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-3 sm:p-4 text-white shadow-xl transition-all">
            <div class="flex items-start justify-between gap-2">
              <p
                class="text-xs sm:text-sm text-white/90 leading-relaxed break-words"
                :class="{ 'line-clamp-2': !isCaptionExpanded }"
              >
                {{ review.details }}
              </p>
              <button
                v-if="review.details && review.details.length > 80"
                type="button"
                class="text-[11px] text-primary font-bold shrink-0 hover:underline cursor-pointer"
                @click="isCaptionExpanded = !isCaptionExpanded"
              >
                {{ isCaptionExpanded ? 'Less' : 'More' }}
              </button>
            </div>

            <!-- Tags in Lightbox -->
            <div v-if="review.tags && review.tags.length" class="flex flex-wrap gap-1.5 mt-2">
              <span
                v-for="tag in review.tags"
                :key="tag"
                class="badge badge-xs sm:badge-sm bg-white/15 border-none text-white/80"
              >
                #{{ tag }}
              </span>
            </div>
          </div>
        </footer>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.scale-fade-enter-active,
.scale-fade-leave-active {
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}
.scale-fade-enter-from {
  opacity: 0;
  transform: scale(0.96);
}
.scale-fade-leave-to {
  opacity: 0;
  transform: scale(1.04);
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
